<template>
    <div id="broadcast-requests-wa-business">
        <div style="background-color: #ffffff" class="d-flex">
            <div class="main-content">
                <div v-if="formVisible">
                    <NewBroadcastMessage
                        :on-success="addItem"
                        :on-cancel="() => (formVisible = false)"
                        :channel="channel"
                    />
                </div>
                <div v-else>
                    <div class="toolbar">
                        <div class="segmented-btn-container">
                            <div class="btn" @click="archiveMode = false" :class="{ active: !archiveMode }">
                                <div class="container">
                                    <span class="label"> {{ $t("wp.active") }} </span>
                                    <span class="badge">
                                        <div class="badge-container">{{ stats.active }}</div>
                                    </span>
                                </div>
                            </div>
                            <div class="btn" @click="archiveMode = true" :class="{ active: archiveMode }">
                                <div class="container">
                                    <div class="label">
                                        <span class="material-icons mr-1"> archive </span>
                                        <span>{{ $t("wp.archived") }}</span>
                                    </div>
                                    <span class="badge">
                                        <div class="badge-container">{{ stats.archived }}</div>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <a-button type="primary" @click="createBroadcastMessage">
                            {{ $t("wp.createBroadcastMessage") }}
                        </a-button>
                    </div>
                    <div class="w-100 d-flex justify-content-end mt-3"></div>

                    <div class="m-5">
                        <a-table
                            :locale="{ emptyText: $t('message.noData') }"
                            :key="'vtable-' + renderCount"
                            :rowKey="`_id`"
                            :columns="cols"
                            :dataSource="items"
                            :pagination="false"
                            :customRow="
                                (record) => {
                                    return {
                                        props: {},
                                        on: {
                                            mouseover: (event) => {
                                                rowMouseOver(record);
                                            },
                                            mouseleave: (event) => {
                                                rowMouseLeave(record);
                                            }, // mouse leave row
                                            click: () => {
                                                showDetailRecord(record);
                                            },
                                        },
                                    };
                                }
                            "
                        >
                            <template slot="avatar" slot-scope="text, record">
                                <div
                                    class="user-segment-avatar text-center"
                                    :class="{ 'user-segment-avatar-odd': isRowNumberOdd(record) }"
                                >
                                    <md-highlight-text md-term="term" class="text-center">
                                        {{ (record.title || "").slice(0, 2).toUpperCase() }}
                                    </md-highlight-text>
                                </div>
                            </template>
                            <span slot="description" slot-scope="text, record">
                                <span
                                    class="text-truncate"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="record.description"
                                    >{{ record.description.slice(0, 15) + "..." }}</span
                                >
                            </span>

                            <span slot="numbers" slot-scope="text, record">{{
                                !!record.all
                                    ? `${$_.get(contacts, "length")} ${$t("wp.numbers")}`
                                    : $_.get(record, "customPhoneNumbers.length")
                                    ? `${$_.get(record, "customPhoneNumbers.length")} ${$t("wp.numbers")}`
                                    : `${$t("wp.noNumbersAdded")}`
                            }}</span>

                            <span slot="messages" slot-scope="text, record">{{
                                $_.get(record, "messages.length") > 1
                                    ? `${$_.get(record, "messages.length")} ${$t("wp.messages")}`
                                    : `${$_.get(record, "messages.length")} ${$t("wp.message")}`
                            }}</span>
                            <span slot="successMessageRatio" slot-scope="text, record">
                                <b-progress
                                    :max="100"
                                    show-value
                                    v-if="getTotalMessageSent(record) > 0"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getSuccessRatioTooltipText(record)"
                                >
                                    <b-progress-bar :value="getSuccessMessageRatio(record)" variant="success">
                                        <span>
                                            {{ `${getSuccessMessageRatio(record)}%` }}
                                        </span>
                                    </b-progress-bar>
                                    <b-progress-bar :value="100 - getSuccessMessageRatio(record)" variant="danger">
                                        <span>
                                            {{ `${100 - getSuccessMessageRatio(record)}%` }}
                                        </span>
                                    </b-progress-bar>
                                </b-progress>
                                <b-progress :max="100" show-value v-else>
                                    <b-progress-bar :value="0" variant="success" />
                                </b-progress>
                            </span>
                            <span slot="usageCost" slot-scope="text, record">
                                {{ `${getUsageCost(record)} ₺` }}
                            </span>
                            <span slot="createdAt" slot-scope="text, record">{{ createdAtHumanize(record) }}</span>
                            <span slot="progress" slot-scope="text, record">
                                <a-progress
                                    :percent="getPercent(record)"
                                    size="small"
                                    status="normal"
                                    v-if="record.status === 'created'"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getProgressText(record)"
                                />
                                <a-progress
                                    :percent="getPercent(record)"
                                    size="small"
                                    status="normal"
                                    v-if="record.status === 'scheduled'"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getProgressText(record)"
                                />
                                <a-progress
                                    :percent="getPercent(record)"
                                    size="small"
                                    status="normal"
                                    v-if="record.status === 'queued'"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getProgressText(record)"
                                />
                                <a-progress
                                    :percent="getPercent(record)"
                                    size="small"
                                    status="active"
                                    v-if="record.status === 'running'"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getProgressText(record)"
                                />
                                <a-progress
                                    :percent="getPercent(record)"
                                    size="small"
                                    status="success"
                                    v-if="record.status === 'completed'"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getProgressText(record)"
                                />
                                <a-progress
                                    :percent="getPercent(record)"
                                    size="small"
                                    status="exception"
                                    v-if="record.status === 'failed'"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getProgressText(record)"
                                />
                                <a-progress
                                    :percent="getPercent(record)"
                                    size="small"
                                    status="exception"
                                    v-if="record.status === 'cancelled'"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getProgressText(record)"
                                />
                            </span>
                            <span slot="status" slot-scope="text, record">
                                <a-badge color="cyan" :text="$t('wp.created')" v-if="record.status === 'created'" />
                                <a-badge
                                    color="volcano"
                                    :text="$t('wp.scheduled')"
                                    v-if="record.status === 'scheduled'"
                                />
                                <a-badge color="gray" :text="$t('wp.queued')" v-if="record.status === 'queued'" />
                                <a-badge color="blue" :text="$t('wp.running')" v-if="record.status === 'running'" />
                                <a-badge
                                    color="green"
                                    :text="$t('wp.completed')"
                                    v-if="record.status === 'completed'"
                                />
                                <a-badge
                                    color="red"
                                    :text="$t('wp.failed')"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="getReasonMessage(record)"
                                    v-if="record.status === 'failed'"
                                />
                                <a-badge
                                    color="yellow"
                                    :text="$t('wp.cancelled')"
                                    v-if="record.status === 'cancelled'"
                                />
                            </span>
                            <span
                                slot-scope="text, record"
                                class="d-flex flex-row justify-content-center operation-buttons"
                                slot="operation"
                            >
                                <span
                                    v-show="archiveMode === false"
                                    class="material-icons mx-2 text-primary operation-button"
                                    @click="() => markAsArchived(record)"
                                    v-if="!archiveMode && !['queued', 'running', 'created'].includes(record.status)"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('message.archive')"
                                >
                                    archive
                                </span>
                                <span
                                    class="material-icons mx-2 text-secondary operation-button"
                                    @click="showDetailRecord(record)"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('wp.broadcast.detail')"
                                >
                                    document_scanner
                                </span>
                                <span
                                    class="material-icons mx-2 text-danger operation-button"
                                    @click="() => cancelBroadcast(record)"
                                    v-if="!['completed', 'failed', 'cancelled'].includes(record.status)"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('wp.cancel')"
                                >
                                    cancel
                                </span>
                            </span>
                        </a-table>
                    </div>

                    <a-modal
                        centered
                        :visible="!!selectedRowRecord"
                        :footer="null"
                        :closable="true"
                        @cancel="selectedRowRecord = null"
                        v-if="selectedRowRecord"
                        class="details"
                        width="1080px"
                    >
                        <BroadcastRequestViewer
                            :request="selectedRowRecord"
                            @newBroadcastCreated="newBroadcastCreated"
                        />
                    </a-modal>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppBreadCrumb from "../../../../../components/common/AppBreadCrumb";
import { mapState } from "vuex";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import log2 from "../../../../../utils/log2";
import WHATSAPP_API from "../../../../../api/whatsapp";
import moment from "moment";
import _ from "lodash";
import NewBroadcastMessage from "../../../Whatsapp/pages/broadcast/create/NewBroadcastMessage.vue";
import BroadcastRequestViewer from "./BroadcastRequestViewer.vue";

const log = log2("BroadcastRequests");
export default {
    name: "BroadcastRequests",
    components: {
        BroadcastRequestViewer,
        NewBroadcastMessage,
        AppBreadCrumb,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
            wallet: (state) => state.wallet.wallet,
            contacts: (state) => {
                return [];
            },
        }),
    },
    data() {
        return {
            archiveMode: false,
            formVisible: false,
            lastActiveItems: [],
            lastArchivedItems: [],
            items: [],
            selectedRowId: null,
            selectedRowRecord: null,
            cols: [
                {
                    dataIndex: "title",
                    key: "title",
                    title: this.$t("wp.title"),
                },
                {
                    dataIndex: "description",
                    key: "description",
                    slots: { title: "description" },
                    scopedSlots: { customRender: "description" },
                    title: this.$t("wp.description"),
                },
                {
                    dataIndex: "createdAt",
                    key: "createdAt",
                    title: this.$t("wp.createdAt"),
                    scopedSlots: { customRender: "createdAt" },
                },
                {
                    dataIndex: "messages",
                    title: this.$t("wp.broadcast.messagesPerContact"),
                    key: "messages",
                    slots: { title: "messages" },
                    scopedSlots: { customRender: "messages" },
                },
                {
                    dataIndex: "successMessageCount",
                    title: this.$t("wp.broadcast.successRatio"),
                    key: "successMessageCount",
                    slots: { title: "successMessageRatio" },
                    scopedSlots: { customRender: "successMessageRatio" },
                },
                {
                    dataIndex: "usageCost",
                    title: this.$t("wp.broadcast.usageCost"),
                    key: "usageCost",
                    slots: { title: "usageCost" },
                    scopedSlots: { customRender: "usageCost" },
                },
                {
                    dataIndex: "numbers",
                    key: "numbers",
                    title: this.$t("wp.numbers"),
                    slots: { title: "numbers" },
                    scopedSlots: { customRender: "numbers" },
                },
                {
                    title: this.$t("wp.broadcast.status"),
                    slots: { title: "status" },
                    scopedSlots: { customRender: "status" },
                },
                {
                    title: this.$t("wp.broadcast.progress"),
                    slots: { title: "progress" },
                    scopedSlots: { customRender: "progress" },
                },
                {
                    title: this.$t("wp.actions"),
                    dataIndex: "operation",
                    key: "operation",
                    slots: { title: "" },
                    scopedSlots: { customRender: "operation" },
                },
            ],
            renderCount: 0,
            stats: {
                archived: 0,
                active: 0,
            },
            isMoreContacts: false,
        };
    },
    methods: {
        newBroadcastCreated(message) {
            this.items = [message, ...this.items];
            this.selectedRowRecord = null;
            setTimeout(() => {
                this.selectedRowRecord = message;
            }, 2000);
        },
        async fetchAll() {
            const isArchived = this.archiveMode;
            const request = isArchived
                ? WHATSAPP_API.BROADCAST.listArchivedBroadcastMessage
                : WHATSAPP_API.BROADCAST.listActiveBroadcastMessage;
            request().then((res) => {
                this.items = res.data;
                if (isArchived) {
                    this.lastArchivedItems = res.data;
                } else {
                    this.lastActiveItems = res.data;
                }
                this.renderCount++;
            });
        },
        async fetchAllLite() {
            const isArchived = this.archiveMode;
            const request = isArchived
                ? WHATSAPP_API.BROADCAST.listArchivedBroadcastMessageLite
                : WHATSAPP_API.BROADCAST.listActiveBroadcastMessageLite;
            request().then((res) => {
                if (_.isEmpty(res.data)) return;
                const map = res.data.reduce((acc, x) => {
                    acc[x._id] = x;
                    return acc;
                }, {});
                this.items = this.items.map((item) => {
                    if (map[item._id]) {
                        return {
                            ...item,
                            ...map[item._id],
                        };
                    }
                    return item;
                });

                if (isArchived) {
                    this.lastArchivedItems = this.lastArchivedItems.map((item) => {
                        if (map[item._id]) {
                            return {
                                ...item,
                                ...map[item._id],
                            };
                        }
                        return item;
                    });
                } else {
                    this.lastActiveItems = this.lastActiveItems.map((item) => {
                        if (map[item._id]) {
                            return {
                                ...item,
                                ...map[item._id],
                            };
                        }
                        return item;
                    });
                }
                this.renderCount++;
            });
        },
        createBroadcastMessage() {
            if (this.wallet.whatsapp.amount < -100) {
                this.$notification.error({
                    message: "Error",
                    description: "Whatsapp Hesap Bakiyeniz Yetersiz.",
                });
                return;
            }
            this.formVisible = true;
        },
        addItem(item) {
            this.items = [item, ...this.items];
            this.formVisible = false;
            this.archiveMode = false;
            this.stats.active++;
            sendSocketMessage("user whatsapp-broadcast-message-created", {
                siteId: this.siteId,
                broadcastId: item._id,
            });
        },
        isRowNumberOdd(record) {
            if (!!this.segments)
                return (this.segments.findIndex((segment) => segment._id === record._id) + 1) % 2 !== 0;
        },
        editRecord(record) {
            log("editRecord", record);
        },
        showArchiveConfirm(record) {
            log("showArchiveConfirm", record);
        },
        rowMouseOver(record) {
            this.selectedRowId = record._id;
        },
        rowMouseLeave(record) {
            this.selectedRowId = null;
        },
        getPercent(record) {
            const { total, sent, failed } = record;
            const totalSent = sent + failed;
            const percent = total !== 0 ? (totalSent / total) * 100 : 0;
            return parseFloat(percent.toFixed(2));
        },
        getTotalMessageSent(record) {
            const { successMessageCount, failedMessageCount } = record;
            return (successMessageCount || 0) + (failedMessageCount || 0);
        },
        getSuccessMessageRatio(record) {
            const { successMessageCount, failedMessageCount } = record;
            const total = successMessageCount + failedMessageCount;
            const percent = total !== 0 ? (successMessageCount / total) * 100 : 0;
            return parseFloat(percent.toFixed(0));
        },
        getUsageCost(record) {
            const { successMessageCount, basePricePerMessage } = record;

            return ((successMessageCount || 0) * (basePricePerMessage || 0)).toFixed(2);
        },
        getProgressText(record) {
            const percent = this.getPercent(record);
            const { total, sent, failed } = record;
            return `${percent}% (${sent} sent/${total} contacts)`;
        },
        getSuccessRatioTooltipText(record) {
            const { successMessageCount, failedMessageCount } = record;
            const total = successMessageCount + failedMessageCount;

            return `Total Send Message = ${total} \n Success Message = ${successMessageCount} \n Failed Message = ${failedMessageCount}`;
        },

        createdAtHumanize(record) {
            return moment(record.createdAt).format("DD MMM YYYY HH:mm");
        },
        markAsArchived(record) {
            WHATSAPP_API.BROADCAST.markArchiveBroadcastMessage(record._id)
                .then((res) => {
                    this.$message.success(this.$t("wp.broadcastMessageArchived"));
                    this.items = this.items.filter((x) => x._id !== record._id);
                    this.stats.archived++;
                    this.stats.active--;
                    this.startFetchStats();
                })
                .catch((err) => {
                    this.$message.error(this.$t("wp.broadcastMessageNotArchived"));
                });
        },
        cancelBroadcast(record) {
            sendSocketMessage("user cancel-broadcast-message", {
                siteId: this.siteId,
                broadcastId: record._id,
            });
            this.$message.success("Broadcast message cancel request was sent. Please wait...");
        },
        fetchStats() {
            WHATSAPP_API.BROADCAST.getBroadcastMessageCountStats().then((res) => {
                this.stats = res.data;
            });
        },
        getReasonMessage(record) {
            return record.reason || "N/A";
        },
        startFetchStats() {
            if (this.statTimerId) clearInterval(this.statTimerId);
            this.fetchStats();
            this.statTimerId = setInterval(() => {
                this.fetchStats();
            }, 5000);
        },
        updateStatus() {
            const validStatusList = ["scheduled", "created", "queued", "running"];
            const waitingItems = this.items.filter((x) => validStatusList.includes(x.status)).map((x) => x._id);
            if (_.isEmpty(waitingItems)) return;
            WHATSAPP_API.BROADCAST.getStatus(waitingItems).then((res) => {
                if (_.isEmpty(res.data)) return;
                const map = res.data.reduce((acc, x) => {
                    acc[x._id] = x;
                    return acc;
                }, {});
                this.items = this.items.map((item) => {
                    if (map[item._id]) {
                        return {
                            ...item,
                            ...map[item._id],
                        };
                    }
                    return item;
                });
            });
        },
        showDetailRecord(record) {
            this.selectedRowRecord = record;
        },
    },
    mounted() {
        log("mounted", this.site);
        this.fetchStats();
        this.fetchAll();
        this.startFetchStats();

        this.timerId = setInterval(() => {
            this.updateStatus();
        }, 1000);
    },
    beforeDestroy() {
        clearInterval(this.timerId);
        clearInterval(this.statTimerId);
    },
    watch: {
        site(site) {
            if (site) {
                log("watch site", site);
            }
        },
        archiveMode(isArchived) {
            clearInterval(this.timerId);
            this.items = isArchived ? this.lastArchivedItems : this.lastActiveItems;
            this.fetchAll();
            this.updateStatus();
            this.timerId = setInterval(() => {
                this.updateStatus();
            }, 1000);
        },
    },
};
</script>

<style lang="scss" scoped>
#broadcast-requests-wa-business {
    .main-content {
        padding: 10px;
        width: 100%;
        background: #fff;
        height: 100%;
        max-height: 100vh;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .segmented-btn-container {
            display: inline-flex;
            flex-wrap: wrap;
            border: 1px solid #d0d9dd;
            border-radius: 6px;
            background: #f0f4f7;
            padding: 2px 2px 2px 0;
            font-size: 14px;
            font-weight: 400;
            color: #5a5f72;
            line-height: 24px;

            .btn {
                display: inline-flex;
                align-items: center;
                position: relative;

                .label {
                    margin-right: 4px;
                    white-space: nowrap;
                    color: #24252d;
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    .material-icons {
                        font-size: 15px;
                        margin-top: 2px;
                    }
                }

                .badge {
                    display: inline-block;
                    font-size: 11px;
                    font-weight: 700;
                    line-height: 16px;
                    padding: 0 4px;
                    border-radius: 4px;
                    letter-spacing: -0.24px;
                    min-height: 16px;
                    text-transform: uppercase;
                    width: fit-content;
                    align-items: center;
                    text-decoration: none;
                    height: fit-content;
                    background-color: #f3f5f6;
                    color: #5a5f72;

                    .badge-container {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        color: #5a5f72;
                    }
                }

                .container {
                    display: flex;
                    padding: 3px 8px;
                    background: #f3f5f6;
                    border-radius: 4px;
                    height: 30px;
                    cursor: pointer;
                    font-size: 14px;
                    line-height: 24px;
                    letter-spacing: -0.24px;
                    font-weight: 600;
                    margin-left: 1px;
                    margin-right: 1px;
                    transition: background 0.2s cubic-bezier(0.645, 0.045, 0.355, 1) 0s;
                    align-items: center;

                    &:hover {
                        background: #e6f0f6;
                        color: #1999e3;
                    }
                }

                &.active {
                    .container {
                        color: #24252d;
                        background: #ffffff;
                        z-index: 1;
                        box-shadow: 0 1px 4px rgb(90 95 114 / 30%);
                    }
                }
            }
        }
    }

    #broadcast-requests .ant-table-thead > tr > th,
    #broadcast-requests .ant-table-tbody > tr > td {
        padding: 0px !important;
        overflow-wrap: break-word !important;
    }

    .ant-table table {
        width: 100% !important;
        text-align: left !important;
        border-radius: 4px 4px 0 0 !important;
        border-collapse: separate !important;
        border-spacing: 0 !important;
    }

    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
        padding: 16px 16px !important;
        overflow-wrap: break-word !important;
    }

    .ant-table-tbody > tr > td {
        padding: 16px 16px !important;
        border-bottom: 1px solid #e8e8e8 !important;
        -webkit-transition: background 0.3s !important;
        transition: background 0.3s !important;
    }

    .ant-table-row-cell-break-word {
        word-wrap: break-word !important;
        word-break: break-word !important;
    }

    .ant-table {
        -webkit-box-sizing: border-box !important;
        box-sizing: border-box !important;
        margin: 0 !important;
        padding: 0 !important;
        color: rgba(0, 0, 0, 0.65) !important;
        font-size: 14px !important;
        font-variant: tabular-nums !important;
        line-height: 1.5 !important;
        list-style: none !important;
        -webkit-font-feature-settings: "tnum" !important;
        font-feature-settings: "tnum" !important;
        position: relative !important;
        clear: both !important;
    }

    .ant-card {
        -webkit-box-sizing: border-box !important;
        box-sizing: border-box !important;
        margin: 0 !important;
        padding: 0 !important;
        color: rgba(0, 0, 0, 0.65) !important;
        font-size: 14px !important;
        font-variant: tabular-nums !important;
        line-height: 1.5 !important;
        list-style: none !important;
        -webkit-font-feature-settings: "tnum" !important;
        font-feature-settings: "tnum" !important;
        position: relative !important;
        background: #fff !important;
        border-radius: 2px !important;
        -webkit-transition: all 0.3s !important;
        transition: all 0.3s !important;
    }

    .ant-table-body {
        max-width: 1050px !important;
        overflow-x: scroll !important;

        ant-table-thead {
            margin-bottom: 10px !important;
        }

        tr {
            padding: 16px 16px !important;
            overflow-wrap: break-word !important;
        }

        td {
            font-family: Poppins !important;
            font-size: 12px !important;
            font-weight: 400 !important;
            color: #181c32 !important;
            border-bottom: none !important;
            padding: 16px 16px !important;
            overflow-wrap: break-word !important;
        }

        th {
            border-right: unset !important;
            border-bottom: 1px solid #e8e8e8 !important;
            padding: 0px 16px !important;
            overflow-wrap: break-word;

            &:last-child {
                border-right: unset !important;
            }
        }
    }

    .ant-table-body tbody td {
        border-right: unset !important;
        padding: 16px 16px !important;

        &:last-child {
            border-right: unset !important;
        }
    }

    .ant-table-body td {
        padding: 16px 16px !important;
    }

    .ant-table-thead > tr > th {
        background: #ffffff;
    }

    .operation-buttons {
        cursor: pointer;

        &.hidden {
            .operation-button {
                display: none;
            }
        }
    }

    .user-segment-avatar {
        display: flex;
        align-items: center;
        font-size: 16px;
        background: #6899c1;
        border-radius: 50%;
        height: 35px;
        width: 35px;
    }

    .user-segment-avatar-odd {
        display: flex;
        align-items: center;
        font-size: 16px;
        background: #c980a3;
        border-radius: 50%;
        height: 35px;
        width: 35px;
    }
}

.details {
    background: #fff;
    border-radius: 6px;
    display: -webkit-box;
    display: -ms-flexbox;
    display: block;
    padding: 40px 10px 20px;
    position: relative;

    .ant-modal {
        width: 100%;
        display: inline-block;

        .ant-modal-content {
            display: inline-table !important;
            max-height: 1200px;
            width: 100%;
        }
    }
}
</style>
